import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  useToast,
  type Column,
} from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import {
  CAP,
  useAutomates,
  useConfirmMapping,
  useEnAttente,
  useMappings,
} from "./api";
import type { MappingAutomate, ResultatEnAttente } from "./types";
import { PageHeader } from "./components/PageHeader";
import { QueryBoundary } from "./components/QueryBoundary";
import { fmtDateTime, idStr, motifTone } from "./lib";

/**
 * MonitorPage — the message/ingestion monitor: the never-drop quarantine worklist (filterable by
 * motif) and the per-analyzer code→ParametreLabo mapping table. The quarantine resolution path opens
 * the per-analyzer mappings so an admin can confirm the missing code (LIS-5). 5-state DoD throughout.
 */
export function MonitorPage() {
  const { t } = useT();
  const [tab, setTab] = useState("quarantaine");

  return (
    <div>
      <PageHeader title={t("lis.monitor.title")} subtitle={t("lis.monitor.subtitle")} />
      <Tabs
        aria-label={t("lis.monitor.title")}
        value={tab}
        onChange={setTab}
        items={[
          { id: "quarantaine", label: t("lis.monitor.tab.quarantaine"), content: <QuarantaineTab /> },
          { id: "mappings", label: t("lis.monitor.tab.mappings"), content: <MappingsTab /> },
        ]}
      />
    </div>
  );
}

// ── Quarantine worklist ──────────────────────────────────────────────────────────
const MOTIFS = ["code_inconnu", "orphelin", "discordance_grave"] as const;

function QuarantaineTab() {
  const { t } = useT();
  const [motif, setMotif] = useState("");
  const q = useEnAttente(motif || undefined);

  return (
    <Card
      padded={false}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: token.s3, flexWrap: "wrap" }}>
          {t("lis.monitor.tab.quarantaine")}
          <Select
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            aria-label={t("lis.monitor.col.motif")}
            style={{ inlineSize: "auto" }}
          >
            <option value="">{t("lis.monitor.motif.all")}</option>
            {MOTIFS.map((m) => (
              <option key={m} value={m}>
                {t(`lis.monitor.motif.${m}`)}
              </option>
            ))}
          </Select>
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            {t("lis.action.refresh")}
          </Button>
        </span>
      }
    >
      <QueryBoundary q={q}>
        {(rows) => <QuarantaineTable rows={rows} />}
      </QueryBoundary>
    </Card>
  );
}

function QuarantaineTable({ rows }: { rows: ResultatEnAttente[] }) {
  const { t } = useT();
  const columns: ReadonlyArray<Column<ResultatEnAttente>> = [
    {
      key: "recu",
      header: t("lis.monitor.col.recu"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.received_at,
      cell: (r) => <span className="nv-ltr-island">{fmtDateTime(r.received_at)}</span>,
    },
    {
      key: "motif",
      header: t("lis.monitor.col.motif"),
      sortable: true,
      sortAccessor: (r) => String(r.motif),
      cell: (r) => <Badge tone={motifTone(r.motif)}>{t(`lis.monitor.motif.${r.motif}`)}</Badge>,
    },
    {
      key: "code",
      header: t("lis.monitor.col.code"),
      mono: true,
      cell: (r) => r.code_analyseur_brut ?? t("lis.na"),
    },
    {
      key: "specimen",
      header: t("lis.monitor.col.specimen"),
      mono: true,
      cell: (r) => r.specimen_ref ?? t("lis.na"),
    },
    {
      key: "valeur",
      header: t("lis.monitor.col.valeur"),
      mono: true,
      cell: (r) => r.valeur_brute ?? t("lis.na"),
    },
  ];

  return (
    <Table<ResultatEnAttente>
      columns={columns}
      rows={rows}
      rowKey={(r) => idStr(r.id)}
      pageSize={15}
      emptyTitle={t("lis.monitor.empty.title")}
      emptyDescription={t("lis.monitor.empty.desc")}
      caption={t("lis.monitor.title")}
    />
  );
}

// ── Per-analyzer mappings + confirm ───────────────────────────────────────────────
function MappingsTab() {
  const { t } = useT();
  const automatesQuery = useAutomates();
  const [automateId, setAutomateId] = useState<string>("");
  const mappingsQuery = useMappings(automateId || null);
  const canConfigure = useCan(CAP.configure);

  const [confirmTarget, setConfirmTarget] = useState<MappingAutomate | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
      <QueryBoundary q={automatesQuery} loadingVariant="spinner">
        {(automates) => (
          <FormField label={t("lis.monitor.mappings.pick")}>
            {(p) => (
              <Select
                {...p}
                value={automateId}
                onChange={(e) => setAutomateId(e.target.value)}
                placeholder={t("lis.monitor.mappings.pick")}
                options={automates.map((a) => ({
                  value: idStr(a.id),
                  label: `${a.code} · ${a.libelle}`,
                }))}
              />
            )}
          </FormField>
        )}
      </QueryBoundary>

      {automateId && (
        <Card padded={false}>
          <QueryBoundary q={mappingsQuery}>
            {(mappings) => (
              <MappingsTable
                mappings={mappings}
                canConfigure={canConfigure}
                onConfirm={setConfirmTarget}
              />
            )}
          </QueryBoundary>
        </Card>
      )}

      {confirmTarget && (
        <ConfirmMappingModal
          automateId={automateId}
          mapping={confirmTarget}
          onClose={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
}

function MappingsTable({
  mappings,
  canConfigure,
  onConfirm,
}: {
  mappings: MappingAutomate[];
  canConfigure: boolean;
  onConfirm: (m: MappingAutomate) => void;
}) {
  const { t } = useT();
  const columns: ReadonlyArray<Column<MappingAutomate>> = [
    {
      key: "code",
      header: t("lis.monitor.mappings.col.code"),
      mono: true,
      sortable: true,
      sortAccessor: (m) => m.code_analyseur,
      cell: (m) => m.code_analyseur,
    },
    {
      key: "parametre",
      header: t("lis.monitor.mappings.col.parametre"),
      mono: true,
      cell: (m) => (m.parametre_labo_id != null ? idStr(m.parametre_labo_id) : t("lis.na")),
    },
    {
      key: "unite",
      header: t("lis.monitor.mappings.col.unite"),
      mono: true,
      cell: (m) => m.unite_canonique ?? m.unite_brute ?? t("lis.na"),
    },
    {
      key: "statut",
      header: t("lis.monitor.mappings.col.statut"),
      sortable: true,
      sortAccessor: (m) => m.statut,
      cell: (m) => (
        <Badge tone={m.statut === "actif" ? "success" : "hold"}>
          {t(`lis.monitor.mappings.statut.${m.statut}`)}
        </Badge>
      ),
    },
    {
      key: "action",
      header: "",
      align: "end",
      cell: (m) =>
        m.statut === "propose" && canConfigure ? (
          <Button variant="secondary" size="sm" onClick={() => onConfirm(m)}>
            {t("lis.monitor.mappings.confirm")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Table<MappingAutomate>
      columns={columns}
      rows={mappings}
      rowKey={(m) => idStr(m.id)}
      pageSize={15}
      emptyTitle={t("lis.monitor.mappings.empty.title")}
      emptyDescription={t("lis.monitor.mappings.empty.desc")}
      caption={t("lis.monitor.tab.mappings")}
    />
  );
}

function ConfirmMappingModal({
  automateId,
  mapping,
  onClose,
}: {
  automateId: string;
  mapping: MappingAutomate;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const confirm = useConfirmMapping(automateId);
  const [parametreId, setParametreId] = useState(
    mapping.parametre_labo_id != null ? idStr(mapping.parametre_labo_id) : "",
  );

  const valid = parametreId.trim().length > 0;

  const submit = () => {
    if (!valid) return;
    confirm.mutate(
      { mappingId: mapping.id, parametre_labo_id: parametreId.trim() },
      {
        onSuccess: () => {
          toast.success(t("lis.monitor.mappings.confirmed"));
          onClose();
        },
        onError: (err) => toast.error(err.message, t("lis.monitor.mappings.confirmError")),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.monitor.mappings.confirmTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("lis.action.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={confirm.isPending} disabled={!valid}>
            {t("lis.action.confirm")}
          </Button>
        </>
      }
    >
      <p style={{ marginBlockStart: 0 }}>
        <span className="nv-mono nv-ltr-island" style={{ color: token.graphite }}>
          {mapping.code_analyseur}
        </span>
      </p>
      <FormField
        label={t("lis.monitor.mappings.parametreId")}
        hint={t("lis.monitor.mappings.parametreId.hint")}
        required
      >
        {(p) => (
          <Input
            {...p}
            mono
            inputMode="numeric"
            value={parametreId}
            onChange={(e) => setParametreId(e.target.value)}
            autoFocus
          />
        )}
      </FormField>
    </Modal>
  );
}
